import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import {
  InputField,
  LightSelectField,
  TextAreaField,
} from "@/components/ui/form";
import { cn, formatNumber, unPick } from "@/lib/utils";
import { useEffect } from "react";
import { Copy, Plus } from "lucide-react";
import type { PaymentInfo } from "@/store/FinanceAccount";
import Avatar from "@/components/ui/avatar";
import { InfoElement } from "@/components/ui/shared/details";
import { Toast } from "@/components/ui/toast";
import Show from "@/components/ui/show";
import PayableTransaction from "@/components/ui/shared/payable-transaction";
import {
  SaleReturnStatusActions,
  SaleReturnStatusColors,
  SaleReturnStatusMap,
  SaleReturnStatusTransitions,
} from "@/components/sale-return/sale-return-meta";
import ProcessPayment from "@/components/ui/shared/process-payment";
import type { SaleReturn } from "@/store/SaleReturn";
import BackButton from "@/components/ui/shared/back-button";

export const Route = createFileRoute("/(dasboard)/orders/returns/$id")({
  component: ViewSaleReturnBlock,
});

function ViewSaleReturnBlock() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const params = Route.useParams();

  const saleReturn = store.saleReturn.current as SaleReturn;

  const formatAmount = (amount: number) => {
    return `${formatNumber(amount)} ${store.auth?.session?.shop?.currency}`;
  };

  const form = useSimpleForm({
    reason: "",
    quantity: "",
    amount: "",
    status: "pending",
    payments: [],
  });

  useEffect(() => {
    store.financeAccount.fetch({});
  }, [store.auth.session]);

  const initData = (sr: SaleReturn) => {
    form.setValues((values: any) => ({
      ...values,
      reason: sr.reason,
      status: sr.status,
      amount: sr.amount,
      quantity: sr.quantity,
      payments: [],
    }));
  };

  useEffect(() => {
    const id = params.id as string;
    if (id) {
      // Find the stock back.
      store.saleReturn
        .fetchOne(id as string)
        .then((sr) => {
          // load the current stock batch.
          store.saleReturn.setCurrent(sr);
          initData(sr);
        })
        .catch((error: any) => {
          store.errors.catch(error);
        });
    }
  }, [params.id, store.auth.session]);

  if (!saleReturn) {
    return undefined;
  }

  const submit = async () => {
    store.errors.reset();
    const data = {
      ...unPick(form.values, ["payments"]),
    };

    if (form.values.payments.length) {
      data["payments"] = form.values.payments.map((item: any) => item);
    }

    store.saleReturn
      .updateCurrent(data)
      .then(() => {
        window.location.reload();
      })
      .catch(store.errors.catch);
  };

  const getRemainingAmount = () => {
    return (
      saleReturn.amount -
      form.values.payments
        .map((item: PaymentInfo) => parseInt(item.amount as any))
        .reduce((sum: number, a: number) => sum + a, 0)
    );
  };

  const addPayment = () => {
    form.setValue("payments", [
      ...form.values.payments,
      {
        finance_account_id: "",
        amount: getRemainingAmount(),
        due_date: "",
        payment_date: formatDate(new Date(), "yyyy-MM-dd"),
        amount_given: "",
        change_given: "",
      },
    ]);
  };

  const statusOptions = SaleReturnStatusActions.filter((item: any) =>
    SaleReturnStatusTransitions[saleReturn.status]?.includes(item.value)
  );
  return (
    <section>
      <Toast
        name="clipboard"
        message={t("Copy to clipboard")}
        autoClose={2000}
      />
      <>{store.errors.render()}</>
      <BackButton />

      <div className="flex ">
        <div
          onClick={async () => {
            await navigator.clipboard.writeText(saleReturn.id);
            store.display.show("clipboard");
          }}
          className="text-xl font-semibold bg-white px-2 py-1 rounded-md border border-gray-200  font-medium hover:text-blue-500 hover:underline text-gray-700 copy-clipboard-parent cursor-pointer mb-3"
        >
          <span>{t("Sale Return ID")} : </span>
          {saleReturn.id}
          <button
            className="ml-2 flex-shrink-0"
            onClick={async () => {
              await navigator.clipboard.writeText(saleReturn.id);
              store.display.show("clipboard");
            }}
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <aside className="bg-white rounded-md border border-gray-200 p-6">
          <article className="mt-2">
            {saleReturn.sale.salable_name == "StockBatch" && (
              <div className="cursor-pointer font-medium  flex items-center gap-3 w-full">
                <Avatar
                  className="h-24 w-24 rounded-lg"
                  name={saleReturn.sale.salable?.product.name as string}
                  url={
                    saleReturn.sale.salable?.product.featured_image?.url != null
                      ? saleReturn.sale.salable.product.featured_image.url
                      : ""
                  }
                />
                <div>
                  <p className="cursor-pointer">
                    {saleReturn.sale.salable.product.name}
                  </p>
                  <p className="text-gray-600">
                    {saleReturn.sale.salable.batch_code}
                  </p>
                </div>
              </div>
            )}

            {saleReturn.sale.salable_name == "Service" && (
              <div className="cursor-pointer font-medium  flex items-center gap-3 w-full">
                <Avatar
                  className="h-24 w-24 rounded-lg"
                  name={saleReturn.sale.salable.name as string}
                  url={
                    saleReturn.sale.salable.featured_image?.url != null
                      ? saleReturn.sale.salable.featured_image.url
                      : ""
                  }
                />
                <div>
                  <p className="cursor-pointer">
                    {saleReturn.sale.salable.name}
                  </p>
                  <div className="rounded-md border-gray-200 bg-gray-100 text-sm inline py-1  px-2">
                    Service
                  </div>
                </div>
              </div>
            )}

            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50/50 p-4 text-sm rounded-md">
              <li className="col-span-2">
                <div className="text-gray-500">{t("Order Id")}</div>
                <div className="font-medium">
                  <p className="cursor-pointer hover:text-blue-800 hover:underline  flex items-center gap-2 copy-clipboard-parent">
                    <span>{saleReturn.order_id}</span>
                    <button
                      className="ml-2 flex-shrink-0 copy-clipboard hidden"
                      onClick={async () => {
                        await navigator.clipboard.writeText(
                          saleReturn.order_id
                        );
                        store.display.show("clipboard");
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </p>
                </div>
              </li>
              <InfoElement
                detail={{
                  label: t("Quantity Ordered"),
                  value: formatNumber(saleReturn.sale.quantity),
                }}
              />
              <InfoElement
                detail={{
                  label: t("Price"),
                  value: formatAmount(saleReturn.sale.selling_price),
                }}
              />
            </ul>

            <Show when={saleReturn.status != "completed"}>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label={t("Quantity")}
                  name="quantity"
                  type="number"
                  value={form.values.quantity}
                  onChange={(e: any) => {
                    store.errors.unset("quantity");
                    const value = e.target.value;
                    if (value > saleReturn.sale.quantity) {
                      store.errors.set(
                        "quantity",
                        t(
                          "The return quantity cannot be greater that ordered quantity"
                        )
                      );
                      form.setValue("quantity", 0);
                    } else {
                      form.setValue("quantity", value);
                    }
                  }}
                />
                <InputField
                  label={t("Amount to refund")}
                  name="amount"
                  type="number"
                  value={form.values.amount}
                  onChange={form.handleChange}
                />

                <TextAreaField
                  name="reason"
                  className="col-span-2"
                  value={form.values.reason}
                  onChange={form.handleChange}
                  label={"Description / Observation"}
                  rows={2}
                />
              </div>
            </Show>
          </article>
        </aside>

        <aside className="p-6 bg-white rounded-md border border-gray-200">
          <header className="text-lg font-semibold">{t("Processing")}</header>
          <article className="mt-2">
            <ul className="col-span-2 mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50/50 p-4 text-sm rounded-md">
              <InfoElement
                detail={{
                  label: t("Quantity"),
                  value: formatNumber(saleReturn.quantity),
                }}
              />
              <InfoElement
                detail={{
                  label: t("Refundable amount"),
                  value: store.shop.formatAmount(
                    saleReturn.quantity * saleReturn.sale.selling_price
                  ),
                }}
              />
              <InfoElement
                detail={{
                  label: t("Amount to refund"),
                  value: store.shop.formatAmount(form.values.amount),
                }}
              />
            </ul>
            <div className="space-x-4 mt-4">
              <label className="font-medium text-sm block">{t("Status")}</label>
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "rounded-full px-8 w-[150px] py-1 text-sm",
                    SaleReturnStatusColors[saleReturn.status]
                  )}
                >
                  {t(SaleReturnStatusMap[saleReturn.status])}
                </div>

                {statusOptions.length > 0 && (
                  <LightSelectField
                    name="status"
                    className="w-full"
                    value={form.values.status}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      if (value == "approved") {
                        addPayment();
                      } else {
                        form.setValue("payments", []);
                      }
                      form.setValue("status", value);
                    }}
                    options={statusOptions}
                  />
                )}
              </div>
            </div>
            {form.values.status == "approved" && (
              <>
                <ProcessPayment
                  form={form}
                  allowCashDetails={true}
                  accountClass="col-span-2"
                  net_amount={saleReturn.amount}
                />
                <Button
                  onClick={addPayment}
                  className="mt-2 border-none bg-transparent text-primary-500 font-semibold hover:underline hover:bg-transparent px-0"
                >
                  <Plus className="w-4 h-4" />
                  {t("Add Payment")}
                </Button>
              </>
            )}

            <Show when={saleReturn.status == "completed"}>
              <PayableTransaction payable={saleReturn} />
            </Show>
          </article>
        </aside>
      </div>

      {saleReturn.status != "completed" && (
        <Button className="mt-4 py-2" onClick={submit}>
          {t("Save the changes")}
        </Button>
      )}
    </section>
  );
}
